/**
 * How It Works Page
 * =================
 * Process explanation and step-by-step guide.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Plug, Brain, Lightbulb, Rocket } from 'lucide-react';
import { Button } from '../../components/shared/Button';
import { SectionHeader } from '../../components/shared/SectionBadge';
import { FadeReveal, StaggerReveal } from '../../components/animations/FadeReveal';
import { HOW_IT_WORKS_STEPS } from '../../utils/constants';

/**
 * How It Works Page Component
 * Explains the DME-CPH process.
 * 
 * @returns {JSX.Element} How It Works page
 */
function HowItWorks() {
  const steps = [
    {
      ...HOW_IT_WORKS_STEPS[0],
      icon: <Plug className="w-10 h-10" />,
      details: [
        'Connect your CRM, SIS, and LMS systems',
        'Import historical enrollment data',
        'Set up automated data sync',
        'Configure security permissions',
      ],
    },
    {
      ...HOW_IT_WORKS_STEPS[1],
      icon: <Brain className="w-10 h-10" />,
      details: [
        'Machine learning models analyze patterns',
        'Predictive algorithms process data',
        'Risk scores are calculated',
        'Trends are identified automatically',
      ],
    },
    {
      ...HOW_IT_WORKS_STEPS[2],
      icon: <Lightbulb className="w-10 h-10" />,
      details: [
        'Receive personalized recommendations',
        'View real-time dashboards',
        'Get automated alerts',
        'Access detailed reports',
      ],
    },
    {
      ...HOW_IT_WORKS_STEPS[3],
      icon: <Rocket className="w-10 h-10" />,
      details: [
        'Implement suggested actions',
        'Track progress over time',
        'Measure ROI and impact',
        'Continuously optimize strategy',
      ],
    },
  ];
  
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-dme-blue via-dme-blue-600 to-dme-blue-800 py-24 lg:py-32">
        <div className="container-dme relative z-10">
          <FadeReveal>
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                How DME-CPH Works
              </h1>
              <p className="text-lg lg:text-xl text-white/80 max-w-2xl mx-auto">
                Get started in minutes and start seeing results with our 
                simple four-step process.
              </p>
            </div>
          </FadeReveal>
        </div>
      </section>
      
      {/* Steps Section */}
      <section className="section bg-white">
        <div className="container-dme">
          <div className="max-w-4xl mx-auto">
            {steps.map((step, index) => (
              <FadeReveal key={step.number} delay={index * 0.1}>
                <div className={`flex flex-col lg:flex-row gap-8 lg:gap-12 mb-16 lg:mb-24 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}>
                  {/* Number & Icon */}
                  <div className="lg:w-1/3 flex flex-col items-center lg:items-start">
                    <div className="relative">
                      <span className="text-8xl lg:text-9xl font-heading font-extrabold text-dme-gray-100">
                        {step.number}
                      </span>
                      <div className="absolute inset-0 flex items-center justify-center lg:justify-start lg:pl-8">
                        <div className="w-20 h-20 rounded-2xl bg-dme-orange flex items-center justify-center text-white shadow-orange">
                          {step.icon}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="lg:w-2/3 text-center lg:text-left">
                    <h3 className="font-heading font-bold text-2xl lg:text-3xl text-dme-blue-800 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-lg text-dme-gray-600 mb-6">
                      {step.description}
                    </p>
                    
                    <ul className="space-y-3">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-3 justify-center lg:justify-start text-dme-gray-600">
                          <Check className="w-5 h-5 text-dme-green flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </FadeReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section bg-dme-blue-50">
        <div className="container-dme">
          <FadeReveal>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading font-bold text-3xl lg:text-4xl text-dme-blue-800 mb-4">
                Ready to Transform Your Enrollment?
              </h2>
              <p className="text-lg text-dme-gray-600 mb-8">
                Get started today and see results in as little as 30 days.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/demo">
                  <Button variant="primary" size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                    Start Free Trial
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="secondary" size="lg">
                    Schedule Demo
                  </Button>
                </Link>
              </div>
            </div>
          </FadeReveal>
        </div>
      </section>
    </div>
  );
}

export default HowItWorks;
